import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Stack, OutlinedInput } from "@mui/material";

OtpInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onComplete: PropTypes.func,
  length: PropTypes.number,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

export default function OtpInput({
  value = "",
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  error = false,
  autoFocus = true,
}) {
  const seed = () => {
    const arr = Array(length).fill("");

    String(value)
      .slice(0, length)
      .split("")
      .forEach((c, i) => {
        arr[i] = c;
      });

    return arr;
  };

  const [cells, setCells] = useState(seed);
  const refs = useRef([]);

  useEffect(() => {
    if (!value) {
      setCells(Array(length).fill(""));
    }
  }, [value, length]);

  const focusCell = (index) => {
    const el = refs.current[index];

    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const emit = (next) => {
    const otpValue = next.join("");

    onChange?.(otpValue);

    if (next.every((d) => d !== "")) {
      onComplete?.(otpValue);
    }
  };

  const handleChange = (index) => (e) => {
    const digits = e.target.value.replace(/\D/g, "");

    if (!digits) {
      const next = [...cells];
      next[index] = "";

      setCells(next);
      emit(next);

      return;
    }

    const next = [...cells];

    let currentIndex = index;

    for (const digit of digits) {
      if (currentIndex >= length) break;

      next[currentIndex] = digit;
      currentIndex++;
    }

    setCells(next);
    emit(next);

    focusCell(Math.min(currentIndex, length - 1));
  };

  const handleKeyDown = (index) => (e) => {
    if (e.key === "Backspace") {
      e.preventDefault();

      const next = [...cells];

      if (cells[index]) {
        next[index] = "";
      } else if (index > 0) {
        next[index - 1] = "";
        focusCell(index - 1);
      }

      setCells(next);
      emit(next);
    }

    if (e.key === "ArrowLeft" && index > 0) {
      focusCell(index - 1);
    }

    if (e.key === "ArrowRight" && index < length - 1) {
      focusCell(index + 1);
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, length);

    if (!pasted) return;

    e.preventDefault();

    const next = Array(length).fill("");

    pasted.split("").forEach((digit, index) => {
      next[index] = digit;
    });

    setCells(next);
    emit(next);

    focusCell(Math.min(pasted.length, length - 1));
  };

  return (
    <Stack
      direction="row"
      spacing={{ xs: 1, sm: 2 }}
      justifyContent="flex-start"
      onPaste={handlePaste}
      sx={{
        width: "100%",
      }}
    >
      {cells.map((digit, index) => (
        <OutlinedInput
          key={index}
          value={digit}
          disabled={disabled}
          error={error}
          autoFocus={autoFocus && index === 0}
          inputRef={(el) => {
            refs.current[index] = el;
          }}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onFocus={(e) => e.target.select()}
          inputProps={{
            inputMode: "numeric",
            maxLength: 1,
            autoComplete: index === 0 ? "one-time-code" : "off",
            "aria-label": `Digit ${index + 1}`,
          }}
          sx={{
            flex: "1 1 0",
            minWidth: 0,

            maxWidth: {
              xs: 48,
              sm: 64,
            },

            height: {
              xs: 48,
              sm: 64,
            },

            backgroundColor: "#FFFFFF",

            "& .MuiOutlinedInput-input": {
              textAlign: "center",
              fontSize: { xs: "20px", sm: "24px" },
              fontWeight: 600,
              padding: 0,
              height: "100%",
              color: "#111827",
            },

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#D9DDE3",
              borderRadius: "12px",
              borderWidth: "1px",
            },

            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#C7CCD4",
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5751E9",
              borderWidth: "2px",
            },

            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: "#EF4444",
            },

            "&.Mui-disabled": {
              backgroundColor: "#F8FAFC",
            },
          }}
        />
      ))}
    </Stack>
  );
}